import { createFileRoute } from '@tanstack/react-router'

import { PageBackButton } from '@/components/common/PageBackButton'
import { IntegrationAccountCard } from '@/components/smartlab/integration-account-card'
import { Card, CardContent } from '@/components/ui/card'
import { useAuth } from '@/hooks/use-auth'
import {
    useGrowerCameraSettings,
    useUpdateGrowerCameraSettings,
} from '@/hooks/use-grower-camera-settings-query'
import {
    useSmartLabIrrigationSettings,
    useUpdateSmartLabIrrigationSettings,
} from '@/hooks/use-smartlab-irrigation-settings-query'
import {
    useSmartLabSettings,
    useUpdateSmartLabSettings,
} from '@/hooks/use-smartlab-settings-query'
import {
    useSmartLabWeightSettings,
    useUpdateSmartLabWeightSettings,
} from '@/hooks/use-smartlab-weight-settings-query'
import { m } from '@/lib/paraglide/messages'

const READ_PERMISSION = 'smartlab.read'
const WRITE_PERMISSION = 'smartlab.write'

export const Route = createFileRoute('/_app/_authed/_sidebar/smartlab/')({
    component: SmartLabSettingsPage,
})

export function SmartLabSettingsPage() {
    const { hasPermission, arePermissionsLoaded } = useAuth()

    if (!arePermissionsLoaded) {
        return (
            <div className="text-sm text-muted-foreground page-pad">
                {m.common_loading()}
            </div>
        )
    }
    if (!hasPermission(READ_PERMISSION)) {
        return <ForbiddenState />
    }
    return <SmartLabSettingsForm />
}

function ForbiddenState() {
    return (
        <Card className="m-4 md:m-6">
            <CardContent className="py-12 text-center space-y-2">
                <h2 className="text-lg font-semibold">
                    {m.smartlab_settings_forbidden_title()}
                </h2>
                <p className="text-sm text-muted-foreground">
                    {m.smartlab_settings_forbidden_desc()}
                </p>
            </CardContent>
        </Card>
    )
}

/**
 * Màn cấu hình SmartLab: bốn tích hợp ĐỘC LẬP trên cùng một trang.
 *
 * Máy vò chè, camera hộ trồng, cân chè và phun nước là bốn bản ghi, bốn
 * endpoint, bốn nút Lưu riêng — sửa tài khoản này không được đụng tới kết nối kia đang chạy sản
 * xuất.
 */
function SmartLabSettingsForm() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission(WRITE_PERMISSION)

    const { data: machineSettings, isLoading: machineLoading } =
        useSmartLabSettings()
    const updateMachine = useUpdateSmartLabSettings()

    const { data: cameraSettings, isLoading: cameraLoading } =
        useGrowerCameraSettings()
    const updateCamera = useUpdateGrowerCameraSettings()

    const { data: weightSettings, isLoading: weightLoading } =
        useSmartLabWeightSettings()
    const updateWeight = useUpdateSmartLabWeightSettings()

    const { data: irrigationSettings, isLoading: irrigationLoading } =
        useSmartLabIrrigationSettings()
    const updateIrrigation = useUpdateSmartLabIrrigationSettings()

    if (machineLoading || cameraLoading || weightLoading || irrigationLoading) {
        return (
            <div className="text-sm text-muted-foreground page-pad">
                {m.common_loading()}
            </div>
        )
    }

    return (
        <div className="max-w-2xl space-y-6 page-pad">
            <div className="flex min-w-0 items-start gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold">
                        {m.smartlab_settings_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.smartlab_settings_description()}
                    </p>
                </div>
            </div>

            <IntegrationAccountCard
                idPrefix="smartlab-machine"
                title={m.smartlab_settings_machine_card()}
                description={m.smartlab_settings_machine_card_desc()}
                enabledHint={m.smartlab_settings_enabled_hint()}
                baseUrlPlaceholder="https://voche.smartlab.io.vn"
                saveLabel={m.smartlab_settings_save_machine()}
                requireHttps={false}
                settings={machineSettings}
                canWrite={canWrite}
                isSaving={updateMachine.isPending}
                onSave={(values) =>
                    updateMachine.mutateAsync({
                        enabled: values.enabled,
                        base_url: values.base_url,
                        username: values.username,
                        // Rỗng = giữ mật khẩu đang lưu (không bao giờ trả về UI).
                        password: values.password ? values.password : undefined,
                    })
                }
            />

            <IntegrationAccountCard
                idPrefix="smartlab-camera"
                title={m.grower_camera_settings_card()}
                description={m.grower_camera_settings_card_desc()}
                enabledHint={m.grower_camera_settings_enabled_hint()}
                baseUrlPlaceholder="https://haodattea.com"
                baseUrlHint={m.grower_camera_settings_base_url_hint()}
                saveLabel={m.grower_camera_settings_save()}
                requireHttps
                settings={cameraSettings}
                canWrite={canWrite}
                isSaving={updateCamera.isPending}
                onSave={(values) =>
                    updateCamera.mutateAsync({
                        enabled: values.enabled,
                        base_url: values.base_url,
                        username: values.username,
                        password: values.password ? values.password : undefined,
                    })
                }
            />

            <IntegrationAccountCard
                idPrefix="smartlab-weight"
                title={m.smartlab_weight_settings_card()}
                description={m.smartlab_weight_settings_card_desc()}
                enabledHint={m.smartlab_weight_settings_enabled_hint()}
                baseUrlPlaceholder="https://canche.smartlab.io.vn"
                saveLabel={m.smartlab_weight_settings_save()}
                requireHttps
                settings={weightSettings}
                canWrite={canWrite}
                isSaving={updateWeight.isPending}
                onSave={(values) =>
                    updateWeight.mutateAsync({
                        enabled: values.enabled,
                        base_url: values.base_url,
                        username: values.username,
                        // Rỗng = giữ mật khẩu đang lưu.
                        password: values.password ? values.password : undefined,
                    })
                }
            />

            <IntegrationAccountCard
                idPrefix="smartlab-irrigation"
                title={m.smartlab_irrigation_settings_card()}
                description={m.smartlab_irrigation_settings_card_desc()}
                enabledHint={m.smartlab_irrigation_settings_enabled_hint()}
                baseUrlPlaceholder="https://tuoihaodat.smartlab.io.vn"
                saveLabel={m.smartlab_irrigation_settings_save()}
                requireHttps
                settings={irrigationSettings}
                canWrite={canWrite}
                isSaving={updateIrrigation.isPending}
                onSave={(values) =>
                    updateIrrigation.mutateAsync({
                        enabled: values.enabled,
                        base_url: values.base_url,
                        username: values.username,
                        // Rỗng = giữ mật khẩu đang lưu.
                        password: values.password ? values.password : undefined,
                    })
                }
            />
        </div>
    )
}
